import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react"
import { mutate } from "swr"
import { setAccessToken, apiPost, apiGet } from "@/api/client"
import type { LoginResponse, RefreshResponse, SwitchVersionResponse, PortalVersion } from "@/types"

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  publisherId: string | null
  email: string | null
  name: string | null
  versions: PortalVersion[]
  switchVersion: (publisherId: string) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}

function resolveApiBase(): string {
  let url = import.meta.env.VITE_API_URL || ""
  if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
    url = `https://${url}`
  }
  return `${url.replace(/\/$/, "")}/api/publisher-portal`
}

const API_BASE = resolveApiBase()
const ACCESS_TOKEN_LIFETIME_MS = 15 * 60 * 1000 // 15 min
const REFRESH_BEFORE_MS = ACCESS_TOKEN_LIFETIME_MS * 0.8 // refresh at 80% (12 min)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [publisherId, setPublisherId] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [name, setName] = useState<string | null>(null)
  const [versions, setVersions] = useState<PortalVersion[]>([])
  const [refreshTimer, setRefreshTimer] = useState<ReturnType<typeof setTimeout> | null>(null)

  const clearAuth = useCallback(() => {
    setAccessToken(null)
    localStorage.removeItem("portal_refresh_token")
    setIsAuthenticated(false)
    setPublisherId(null)
    setEmail(null)
    setName(null)
    setVersions([])
    if (refreshTimer) clearTimeout(refreshTimer)
  }, [refreshTimer])

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer) clearTimeout(refreshTimer)
    const timer = setTimeout(async () => {
      const refreshToken = localStorage.getItem("portal_refresh_token")
      if (!refreshToken) return

      try {
        const res = await fetch(`${API_BASE}/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        })
        if (res.ok) {
          const data: RefreshResponse = await res.json()
          setAccessToken(data.access_token)
          localStorage.setItem("portal_refresh_token", data.refresh_token)
          scheduleRefresh()
        } else {
          clearAuth()
        }
      } catch {
        clearAuth()
      }
    }, REFRESH_BEFORE_MS)
    setRefreshTimer(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(async (loginEmail: string, password: string) => {
    const res = await fetch(`${API_BASE}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: loginEmail, password }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({ detail: "Login failed" }))
      throw new Error(data.detail || "Login failed")
    }

    const data: LoginResponse = await res.json()
    setAccessToken(data.access_token)
    localStorage.setItem("portal_refresh_token", data.refresh_token)
    setIsAuthenticated(true)
    setPublisherId(data.current_publisher_id || data.publisher_id)
    setEmail(data.email)
    setName(data.name)
    setVersions(data.versions || [])
    scheduleRefresh()
  }, [scheduleRefresh])

  const switchVersion = useCallback(async (targetPublisherId: string) => {
    const data = await apiPost<SwitchVersionResponse>("/switch-version", { publisher_id: targetPublisherId })
    setAccessToken(data.access_token)
    localStorage.setItem("portal_refresh_token", data.refresh_token)
    setPublisherId(data.current_publisher_id)
    setVersions(data.versions || [])
    scheduleRefresh()
    // Revalidate all portal data under the new version scope.
    await mutate(() => true, undefined, { revalidate: true })
  }, [scheduleRefresh])

  const logout = useCallback(() => {
    // Fire-and-forget server-side logout
    const token = localStorage.getItem("portal_refresh_token")
    if (token) {
      fetch(`${API_BASE}/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }).catch(() => {})
    }
    clearAuth()
  }, [clearAuth])

  // Auto-refresh on mount if refresh token exists
  useEffect(() => {
    const refreshToken = localStorage.getItem("portal_refresh_token")
    if (!refreshToken) {
      setIsLoading(false)
      return
    }

    fetch(`${API_BASE}/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
      .then(async (res) => {
        if (res.ok) {
          const data: RefreshResponse = await res.json()
          setAccessToken(data.access_token)
          localStorage.setItem("portal_refresh_token", data.refresh_token)

          // Decode publisher_id from JWT payload (base64)
          try {
            const payload = JSON.parse(atob(data.access_token.split(".")[1]))
            setPublisherId(payload.publisher_id)
            setEmail(payload.email)
          } catch { /* ignore decode errors */ }

          setIsAuthenticated(true)
          scheduleRefresh()
          // Hydrate accessible versions from profile (JWT carries only the current pubid).
          apiGet<{ versions?: PortalVersion[] }>("/profile")
            .catch(() => null)
            .then((p) => { if (p?.versions) setVersions(p.versions) })
        } else {
          clearAuth()
        }
      })
      .catch(() => clearAuth())
      .finally(() => setIsLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, publisherId, email, name, versions, switchVersion, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}
